import {
  LBAlertCircleIcon,
  LBBadge,
  LBButton,
  LBChevronDownIcon,
  LBChevronRightIcon,
  LBMergeAccountsIcon,
  LBStopSignIcon,
  LBUnplugIcon,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import _ from 'lodash'
import { type ReactNode } from 'react'

import type {
  Insight,
  InsightSeverity,
  OrgBuyer
} from '../../apis/organizations'
import GitstreamIcon from '../../custom-components/GitstreamIcon'
import SensorsIcon, {
  ICONS_CONFIG,
  type SensorName
} from '../../custom-components/SensorsIcons'
import { formatDateWithRelative } from '../../utils/dateHelpers'
import BuyersHeader from './BuyersHeader'
import ChampionsList, { type Champion } from './ChampionsList'

interface AiToolEntry {
  tool_key?: string
  tool_display_name?: string
  integration_type_name?: string
  events_90d?: number
  last_seen_at?: string | null
  top_footprint_types?: string[]
}

const FOOTPRINT_TYPE_LABELS: Record<string, string> = {
  ai_interaction: 'AI interactions',
  pr_review: 'PR reviews',
  commit_author: 'Commit authors',
  commit_co_author: 'Co-authored commits',
  branch_creation: 'Branches created'
}

const SEVERITY_ACCENT: Record<InsightSeverity, string> = {
  critical: 'border-l-4 border-l-red-500',
  warning: 'border-l-4 border-l-amber-500',
  info: 'border-l-4 border-l-blue-500'
}

const humanizeFootprintType = (value: string): string =>
  FOOTPRINT_TYPE_LABELS[value] ?? _.startCase(value)

const resolveSensorIcon = (toolKey: string | undefined): SensorName | null => {
  const key = toolKey?.toLowerCase().replace(/[^a-z]/g, '')
  return key && key in ICONS_CONFIG ? (key as SensorName) : null
}

const DetailRow = ({
  label,
  children
}: {
  label: string
  children: ReactNode
}) => (
  <div className='flex gap-2'>
    <span className='text-muted-foreground w-44 shrink-0'>{label}</span>
    <span className='text-foreground'>{children}</span>
  </div>
)

interface InsightCardProps {
  insight: Insight
  expanded: boolean
  onToggle: () => void
  buyers: OrgBuyer[]
}

const splitMessage = (msg: string) => {
  const lines = msg.split('\n')
  return {
    body: lines.slice(0, -1).join(' '),
    takeaway: lines[lines.length - 1] ?? ''
  }
}

const MessageBody = ({ body }: { body: string }) => {
  if (!body) {
    return null
  }
  return (
    <div className='text-muted-foreground leading-relaxed mb-3'>{body}</div>
  )
}

const TakeawayQuote = ({ text }: { text: string }) => {
  if (!text) {
    return null
  }
  return (
    <div className='mt-3 border-l-4 border-emerald-400 bg-emerald-50/40 px-3 py-2 text-sm italic text-emerald-900'>
      {text}
    </div>
  )
}

type RatePolarity = 'higher-is-better' | 'lower-is-better' | 'always-positive'

const TONE_CLASSES = {
  positive: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  warning: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  danger: 'bg-red-100 text-red-700 hover:bg-red-100'
} as const

const RateBadge = ({
  value,
  label,
  threshold = 50,
  polarity = 'higher-is-better'
}: {
  value: number
  label: string
  threshold?: number
  polarity?: RatePolarity
}) => {
  const isPositive =
    polarity === 'always-positive' ||
    (polarity === 'lower-is-better'
      ? value < threshold
      : value >= threshold)
  const tone = isPositive
    ? 'positive'
    : polarity === 'lower-is-better'
      ? 'danger'
      : 'warning'
  return (
    <LBBadge
      variant='secondary'
      className={`shrink-0 border-transparent ${TONE_CLASSES[tone]}`}
    >
      {value}
      {label}
    </LBBadge>
  )
}

const InsightCard = ({
  insight,
  expanded,
  onToggle,
  buyers
}: InsightCardProps) => {
  const metadata = insight.metadata ?? {}
  const isDidYouKnow = insight.group === 'did_you_know'

  const buyerEngaged = metadata.buyer_engaged === true

  const isAiIntegrations = insight.category === 'ai_integrations'
  const isGitstreamCoverage = insight.category === 'gitstream_repo_coverage'

  const aiTools: AiToolEntry[] = isAiIntegrations
    ? ((metadata.tools as AiToolEntry[] | undefined) ?? [])
    : []
  const aiToolSensorNames = aiTools
    .map(t => resolveSensorIcon(t.tool_key))
    .filter((name): name is SensorName => name !== null)

  const coverage = isGitstreamCoverage
    ? {
        reposGitstream: Number(metadata.repos_gitstream ?? 0),
        reposTotal: Number(metadata.repos_total ?? 0),
        coveragePct: Number(metadata.coverage_pct ?? 0),
        missingRepos: Number(metadata.missing_repos ?? 0),
        positive90d: Number(metadata.positive_90d ?? 0),
        negative90d: Number(metadata.negative_90d ?? 0),
        commits30d: Number(metadata.commits_30d ?? 0),
        totalCommits30d: Number(metadata.total_commits_30d ?? 0),
        commitsRatioPct: Number(metadata.commits_ratio_pct ?? 0),
        lastFeedbackAt: metadata.last_feedback_at as string | null | undefined,
        lastCommitAt: metadata.last_commit_at as string | null | undefined
      }
    : null

  const resolutionRate =
    insight.category === 'inline_suggestions' &&
    metadata.resolution_rate !== undefined
      ? Number(metadata.resolution_rate)
      : null

  const mergeSuggestionsRatio =
    insight.category === 'merge_suggestions' && metadata.ratio_pct !== undefined
      ? Number(metadata.ratio_pct)
      : null

  const champions: Champion[] =
    insight.category === 'merge_suggestions'
      ? ((metadata.champions as Champion[] | undefined) ?? [])
      : []

  const pmExampleTags: string[] =
    (metadata.example_tags as string[] | undefined) ?? []
  const pmExampleIssueKeys: string[] =
    (metadata.example_issue_keys as string[] | undefined) ?? []
  const pmCustomPattern = metadata.custom_pattern as string | null

  const capacityPct =
    insight.category === 'repo_capacity' &&
    metadata.capacity_pct !== undefined
      ? Number(metadata.capacity_pct)
      : null

  const rawMinProbability = metadata.min_probability
  const minProbabilityNumber =
    rawMinProbability == null ||
    (typeof rawMinProbability === 'string' && rawMinProbability.trim() === '')
      ? Number.NaN
      : Number(rawMinProbability)
  const minProbabilityPercent = Number.isFinite(minProbabilityNumber)
    ? Math.round(minProbabilityNumber * 100)
    : null

  const { body: messageBody, takeaway } = splitMessage(insight.message ?? '')

  const detailsRegionId = `insight-details-${insight.id}`

  return (
    <div
      className={`bg-card rounded-md border border-border shadow-sm p-4 ${SEVERITY_ACCENT[insight.severity]}`}
      data-insight-id={insight.id}
    >
      <div className='flex items-start gap-3'>
        {!isDidYouKnow &&
          (isAiIntegrations && aiToolSensorNames.length > 0 ? (
            <div className='flex items-center gap-1.5 shrink-0'>
              {aiToolSensorNames.slice(0, 3).map((name, index) => (
                <SensorsIcon key={`${name}-${index}`} name={name} size={24} />
              ))}
            </div>
          ) : isGitstreamCoverage ? (
            <GitstreamIcon
              width={32}
              height={32}
              className='shrink-0 -my-1 text-foreground'
            />
          ) : insight.category === 'merge_suggestions' ? (
            <LBMergeAccountsIcon className='h-6 w-6 shrink-0 text-amber-600' />
          ) : insight.category === 'repo_capacity' ? (
            <LBAlertCircleIcon className='h-6 w-6 shrink-0 text-red-600' />
          ) : insight.category === 'deprecated_teams_api_usage' ? (
            <LBStopSignIcon className='h-6 w-6 shrink-0 text-amber-600' />
          ) : insight.category === 'pm_issue_link_gap' ? (
            <LBUnplugIcon className='h-6 w-6 shrink-0 text-red-600' />
          ) : (
            <div className='w-6 h-6 shrink-0' />
          ))}
        {isDidYouKnow && (
          <div
            className='text-2xl shrink-0 leading-none'
            role='img'
            aria-label='Did you know'
          >
            💡
          </div>
        )}
        <div className='flex-1 min-w-0'>
          <div className='flex items-start justify-between gap-3'>
            <div className='text-sm font-semibold text-foreground min-w-0'>
              {insight.title}
            </div>
            <LBButton
              size='sm'
              variant='ghost'
              onClick={onToggle}
              className='shrink-0 gap-1'
              aria-expanded={expanded}
              aria-controls={detailsRegionId}
            >
              {expanded ? (
                <LBChevronDownIcon className='h-4 w-4' />
              ) : (
                <LBChevronRightIcon className='h-4 w-4' />
              )}
              Details
            </LBButton>
          </div>
          <div className='mt-1 flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2 min-w-0'>
              {insight.metric && (
                <span className='text-sm text-muted-foreground'>
                  <span className='font-semibold text-foreground'>
                    {insight.metric.value.toLocaleString()}
                  </span>{' '}
                  {insight.metric.label}
                </span>
              )}
            </div>
            {resolutionRate !== null && (
              <RateBadge value={resolutionRate} label='% resolved' />
            )}
            {mergeSuggestionsRatio !== null && (
              <RateBadge value={mergeSuggestionsRatio} label='% of team' />
            )}
            {capacityPct !== null && (
              <RateBadge
                value={capacityPct}
                label='% of quota'
                threshold={90}
                polarity='lower-is-better'
              />
            )}
          </div>

          {expanded && (
            <div
              id={detailsRegionId}
              role='region'
              className='mt-3 pt-3 border-t border-border/50 space-y-2 text-sm'
            >
              {isDidYouKnow ? (
                <>
                  <MessageBody body={messageBody} />
                  {insight.category === 'inline_suggestions' && (
                    <>
                      <DetailRow label='Repos'>
                        {Number(metadata.repos_30d ?? 0).toLocaleString()}
                      </DetailRow>
                      {(metadata.last_event_at as string | undefined) && (
                        <DetailRow label='Last activity'>
                          {formatDateWithRelative(
                            metadata.last_event_at as string
                          ) ?? '—'}
                        </DetailRow>
                      )}
                    </>
                  )}
                  <TakeawayQuote text={takeaway} />
                </>
              ) : (
                <>
                  {isAiIntegrations && aiTools.length > 0 && (
                    <div className='space-y-5'>
                      {aiTools.map((tool, index) => {
                        const toolSensor = resolveSensorIcon(tool.tool_key)
                        const toolFootprints = tool.top_footprint_types ?? []
                        return (
                          <div
                            key={
                              tool.tool_key ??
                              tool.tool_display_name ??
                              `tool-${index}`
                            }
                            className='space-y-1'
                          >
                            <div className='flex items-center gap-2 text-sm'>
                              {toolSensor && (
                                <SensorsIcon name={toolSensor} size={20} />
                              )}
                              <span className='font-semibold text-foreground'>
                                {tool.tool_display_name}
                              </span>
                              {tool.events_90d !== undefined && (
                                <span className='text-muted-foreground'>
                                  · {tool.events_90d.toLocaleString()} events
                                  (90d)
                                </span>
                              )}
                            </div>
                            {tool.integration_type_name && (
                              <DetailRow label='Integration'>
                                {tool.integration_type_name} (not enabled)
                              </DetailRow>
                            )}
                            {tool.last_seen_at && (
                              <DetailRow label='Last activity'>
                                {formatDateWithRelative(tool.last_seen_at) ??
                                  '—'}
                              </DetailRow>
                            )}
                            {toolFootprints.length > 0 && (
                              <DetailRow label='Top activities'>
                                {toolFootprints
                                  .map(humanizeFootprintType)
                                  .join(', ')}
                              </DetailRow>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {coverage && (
                    <>
                      <MessageBody body={messageBody} />
                      <DetailRow label='Repos enabled'>
                        {coverage.reposGitstream.toLocaleString()} /{' '}
                        {coverage.reposTotal.toLocaleString()} (
                        {coverage.coveragePct}%)
                      </DetailRow>
                      <DetailRow label='Remaining'>
                        {coverage.missingRepos.toLocaleString()}{' '}
                        {coverage.missingRepos === 1 ? 'repo' : 'repos'}
                      </DetailRow>
                      {(coverage.positive90d > 0 ||
                        coverage.negative90d > 0) && (
                        <DetailRow label='Code review feedback'>
                          <LBTooltip>
                            <LBTooltipTrigger asChild>
                              <button
                                type='button'
                                aria-label={`${coverage.positive90d.toLocaleString()} positive, ${coverage.negative90d.toLocaleString()} negative reactions in the last 90 days`}
                                className='inline-flex items-center gap-4 cursor-help bg-transparent p-0 border-0 text-left'
                              >
                                <span
                                  className='text-emerald-700 font-semibold'
                                  aria-hidden='true'
                                >
                                  👍&nbsp;&nbsp;
                                  {coverage.positive90d.toLocaleString()}
                                </span>
                                <span
                                  className='text-foreground'
                                  aria-hidden='true'
                                >
                                  👎&nbsp;&nbsp;
                                  {coverage.negative90d.toLocaleString()}
                                </span>
                              </button>
                            </LBTooltipTrigger>
                            <LBTooltipContent>last 90 days</LBTooltipContent>
                          </LBTooltip>
                        </DetailRow>
                      )}
                      {coverage.lastFeedbackAt && (
                        <DetailRow label='Last feedback'>
                          {formatDateWithRelative(coverage.lastFeedbackAt) ??
                            '—'}
                        </DetailRow>
                      )}
                      {coverage.commits30d > 0 && (
                        <DetailRow label='Inline-suggested commits'>
                          <LBTooltip>
                            <LBTooltipTrigger asChild>
                              <button
                                type='button'
                                aria-label={`${coverage.commits30d.toLocaleString()} of ${coverage.totalCommits30d.toLocaleString()} commits (${coverage.commitsRatioPct}%) in the last 30 days`}
                                className='cursor-help bg-transparent p-0 border-0 text-left'
                              >
                                {coverage.commits30d.toLocaleString()} of{' '}
                                {coverage.totalCommits30d.toLocaleString()} (
                                {coverage.commitsRatioPct}%)
                              </button>
                            </LBTooltipTrigger>
                            <LBTooltipContent>last 30 days</LBTooltipContent>
                          </LBTooltip>
                        </DetailRow>
                      )}
                      {coverage.lastCommitAt && (
                        <DetailRow label='Last commit'>
                          {formatDateWithRelative(coverage.lastCommitAt) ?? '—'}
                        </DetailRow>
                      )}
                    </>
                  )}
                  {insight.category === 'merge_suggestions' && (
                    <>
                      <MessageBody body={messageBody} />
                      {champions.length > 0 && (
                        <DetailRow label='Champions'>
                          {champions.length.toLocaleString()}
                        </DetailRow>
                      )}
                      <DetailRow label='Pending merges'>
                        {Number(
                          metadata.outstanding_suggestions ?? 0
                        ).toLocaleString()}
                      </DetailRow>
                      <DetailRow label='Team users'>
                        {Number(metadata.team_users ?? 0).toLocaleString()}
                      </DetailRow>
                      {minProbabilityPercent !== null && (
                        <DetailRow label='Confidence'>
                          &gt; {minProbabilityPercent}%
                        </DetailRow>
                      )}
                      {champions.length > 0 && (
                        <div className='mt-3'>
                          <ChampionsList champions={champions} />
                        </div>
                      )}
                      <TakeawayQuote text={takeaway} />
                    </>
                  )}
                  {insight.category === 'repo_capacity' && (
                    <>
                      <MessageBody body={messageBody} />
                      <DetailRow label='Connected repos'>
                        {Number(metadata.connected_repos ?? 0).toLocaleString()}
                      </DetailRow>
                      <DetailRow label='SFDC quota'>
                        {Number(metadata.repos_authorized ?? 0).toLocaleString()}
                      </DetailRow>
                      <DetailRow label='Capacity'>
                        {Number(metadata.capacity_pct ?? 0)}%
                      </DetailRow>
                      <TakeawayQuote text={takeaway} />
                    </>
                  )}
                  {insight.category === 'deprecated_teams_api_usage' && (
                    <>
                      <MessageBody body={messageBody} />
                      <DetailRow label='Calls (since May 2026)'>
                        {Number(
                          metadata.num_of_api_calls ?? 0
                        ).toLocaleString()}
                      </DetailRow>
                      {Number(metadata.num_of_distinct_endpoints ?? 0) > 1 && (
                        <DetailRow label='Distinct endpoints'>
                          {Number(
                            metadata.num_of_distinct_endpoints ?? 0
                          ).toLocaleString()}
                        </DetailRow>
                      )}
                      {(metadata.last_call_at as string | undefined) && (
                        <DetailRow label='Last call'>
                          {formatDateWithRelative(
                            metadata.last_call_at as string
                          ) ?? '—'}
                        </DetailRow>
                      )}
                      <TakeawayQuote text={takeaway} />
                    </>
                  )}
                  {insight.category === 'pm_issue_link_gap' && (
                    <>
                      <MessageBody body={messageBody} />
                      <DetailRow label='PM integration'>
                        {(metadata.pm_providers as string) ?? '-'}
                      </DetailRow>
                      <DetailRow
                        label={`Branches checked (${Number(
                          metadata.span_days ?? 30
                        )}d)`}
                      >
                        {Number(metadata.checked ?? 0).toLocaleString()}
                      </DetailRow>
                      <DetailRow label='Of those, tagged'>
                        {Number(metadata.tagged ?? 0).toLocaleString()}
                      </DetailRow>
                      {metadata.is_regression === true && (
                        <DetailRow label='Linked before this'>
                          {`${Number(
                            metadata.baseline_link_rate_pct ?? 0
                          )}% (${Number(
                            metadata.baseline_linked ?? 0
                          ).toLocaleString()} of ${Number(
                            metadata.baseline_tagged ?? 0
                          ).toLocaleString()} tagged)`}
                        </DetailRow>
                      )}
                      {pmExampleTags.length > 0 && (
                        <DetailRow label='Branch tags (e.g.)'>
                          <span className='font-mono text-xs break-all'>
                            {pmExampleTags.join(', ')}
                          </span>
                        </DetailRow>
                      )}
                      {pmExampleIssueKeys.length > 0 && (
                        <DetailRow label='Imported issue keys (e.g.)'>
                          <span className='font-mono text-xs break-all'>
                            {pmExampleIssueKeys.join(', ')}
                          </span>
                        </DetailRow>
                      )}
                      <DetailRow label='Custom tag pattern'>
                        {pmCustomPattern ? (
                          <span className='font-mono text-xs break-all'>
                            {pmCustomPattern}
                          </span>
                        ) : (
                          'Not set (using the default pattern)'
                        )}
                      </DetailRow>
                      <TakeawayQuote text={takeaway} />
                    </>
                  )}
                  {buyerEngaged && buyers.length > 0 && (
                    <div className='mt-3'>
                      <BuyersHeader buyers={buyers} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default InsightCard
